import { useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import { categories, comics } from "@/mocks/comics";
import type { Comic } from "@/mocks/comics";

const PAGE_SIZE = 12;
const siteUrl = "https://teachmanga4.github.io";

type SortKey = "default" | "latest" | "hot" | "rating";

export default function CategoryPage() {
  const { slug = "all" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const sort = (searchParams.get("sort") as SortKey) || "default";
  const pageParam = Number(searchParams.get("page") || 1);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const currentCategory = categories.find((c) => c.slug === slug);

  const filtered = useMemo(() => {
    let list: Comic[] = comics;
    if (slug !== "all") {
      list = list.filter((c) => c.categories.includes(slug));
    }
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.altTitle.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    switch (sort) {
      case "latest":
        list = [...list].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
        break;
      case "hot":
        list = [...list].sort((a, b) => Number(b.views.replace(/[亿万]/g, (m) => (m === "亿" ? "100000000" : "10000"))) - Number(a.views.replace(/[亿万]/g, (m) => (m === "亿" ? "100000000" : "10000"))));
        break;
      case "rating":
        list = [...list].sort((a, b) => b.rating - a.rating);
        break;
      default:
        list = [...list].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    }
    return list;
  }, [slug, query, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedList = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateParams = (next: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value && value !== "default") params.set(key, value);
      else params.delete(key);
    });
    if (next.page === "1") params.delete("page");
    setSearchParams(params);
  };

  const pageTitle = query
    ? `搜索「${query}」漫画结果-178漫画网`
    : `${currentCategory ? currentCategory.name : "全部"}漫画大全-在线免费阅读-178漫画网`;

  const pageDescription = query
    ? `在178漫画网搜索「${query}」的漫画结果，在线免费阅读下拉式漫画，便捷的浏览方式解放您的双手。`
    : `${currentCategory ? currentCategory.name : "海量"}漫画在线免费阅读，下拉式漫画阅读方式，热门漫画持续更新，178漫画网让您轻松畅读《家庭教师》等高分漫画。`;

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: "default", label: "综合排序" },
    { key: "latest", label: "最近更新" },
    { key: "hot", label: "人气最高" },
    { key: "rating", label: "评分最高" },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Seo
        title={pageTitle}
        description={pageDescription}
        keywords="漫画分类,在线免费阅读,下拉式漫画,178漫画网"
        canonical={`${siteUrl}/category/${slug}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: siteUrl },
              { "@type": "ListItem", position: 2, name: currentCategory ? currentCategory.name : "全部漫画", item: `${siteUrl}/category/${slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: pageTitle,
            url: `${siteUrl}/category/${slug}`,
            inLanguage: "zh-CN",
            isPartOf: { "@type": "WebSite", name: "178漫画网", url: siteUrl },
          },
        ]}
      />

      <Navbar />

      <main id="main-content" className="flex-1 pt-16 md:pt-[72px]">
        {/* Header */}
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-6 md:px-6 lg:px-10">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: currentCategory ? currentCategory.name : "全部漫画" }]} />
            <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              {query ? `搜索「${query}」` : currentCategory ? `${currentCategory.name}漫画` : "全部漫画"}
            </h1>
            <p className="mt-2 text-sm text-foreground-700">
              {query
                ? `共找到 ${filtered.length} 部相关漫画`
                : currentCategory
                  ? currentCategory.description
                  : "178漫画网全部漫画，海量资源在线免费阅读，下拉式漫画让阅读更轻松"}
            </p>
          </div>
        </div>

        {/* Category tabs */}
        <div className="sticky top-16 z-30 border-b border-background-200 bg-background-50/95 backdrop-blur-md md:top-[72px]">
          <div className="mx-auto w-full px-4 py-3 md:px-6 lg:px-10">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/category/all"
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${slug === "all" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-800 hover:bg-background-200"}`}
              >
                全部
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${slug === c.slug ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-800 hover:bg-background-200"}`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Toolbar + grid */}
        <div className="mx-auto w-full px-4 py-8 md:px-6 lg:px-10">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-700">
              共 <strong className="text-primary-600">{filtered.length}</strong> 部漫画
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    const params = new URLSearchParams();
                    setSearchParams(params);
                  }}
                  className="ml-3 inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  清除搜索 <i className="ri-close-line" aria-hidden="true" />
                </button>
              )}
            </p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="排序方式">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => updateParams({ sort: opt.key, page: "1" })}
                  className={`whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm transition-colors ${sort === opt.key ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-800 hover:bg-background-200"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {pagedList.length > 0 ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {pagedList.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center rounded-lg border border-background-200 bg-background-100 py-16">
              <i className="ri-search-line text-4xl text-foreground-500" aria-hidden="true" />
              <p className="mt-3 text-sm text-foreground-700">没有找到相关漫画，换个关键词试试吧</p>
              <Link
                to="/category/all"
                className="mt-4 flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览全部漫画
              </Link>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="mt-10 flex items-center justify-center gap-2" aria-label="分页导航">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => updateParams({ page: String(currentPage - 1) })}
                className="flex h-10 w-10 items-center justify-center rounded-md bg-background-100 text-foreground-800 transition-colors hover:bg-background-200 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="上一页"
              >
                <i className="ri-arrow-left-line" aria-hidden="true" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => updateParams({ page: String(p) })}
                  className={`flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium transition-colors ${
                    currentPage === p ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-800 hover:bg-background-200"
                  }`}
                  aria-label={`第 ${p} 页`}
                  aria-current={currentPage === p ? "page" : undefined}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => updateParams({ page: String(currentPage + 1) })}
                className="flex h-10 w-10 items-center justify-center rounded-md bg-background-100 text-foreground-800 transition-colors hover:bg-background-200 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="下一页"
              >
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </button>
            </nav>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}